import React, { useEffect, useState } from 'react';
import { Box, Button, CardContent, styled, Typography } from '@mui/material';
import { ProviderOffersI } from 'src/e2e-redesign/business-logic/e2e-configuration-object-logic';
import ThemedCard from 'src/e2e-redesign/components/ThemedCard';
import { NotOffersI } from 'src/e2e-redesign/views-bread/BreadLenderResponses/bread-logic/bread-provider-response-logic';
import { AppSubLenderResponseE } from 'src/interfaces/submissions.interfaces';
import { checkIfBread, checkIfBreadId } from 'src/utils/bread-e2e-constants';
import BreadMoveForwardModal from './bread-move-forward-modal';

interface WaterfallContinuePipingCardProps {
  onClick: () => void;
  isBread?: boolean;
  isBreadError?: boolean;
  offers: ProviderOffersI[];
  notOffers: NotOffersI[];
}

const StyledContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  [theme.breakpoints.down('sm')]: {
    justifyContent: 'center',
  },
}));

export default function BreadMoveForwardCard({
  onClick,
  isBread = false,
  isBreadError = false,
  offers,
  notOffers,
}: WaterfallContinuePipingCardProps) {
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const notification = localStorage.getItem('notification');
    if (notification === 'provider-response') {
      setModalOpen(true);
    }
  }, []);

  const handleModalClose = () => {
    setModalOpen(false);
    localStorage.removeItem('notification');
  };

  const handleAdditionalOptions = () => {
    handleModalClose();
    onClick();
  };

  const getCardText = () => {
    const breadNotOffers = notOffers.find((no) => checkIfBreadId(no.lender.lender_id));
    let isPending;
    // TODO - i don't know what logic isBreadError is doing and couldn't force a pending so we need to test and clean this up
    const breadPendingMsg = (
      <>
        <b>Proceeding with another provider(s)</b>: By selecting "Additional Options" I agree that I am proceeding with
        additional financing or payment option provider(s). This may result in additional inquries on my credit report.
        Selecting additional options does not cancel the Petland Credit Card and if fully approved, it will still be
        sent to me.
      </>
    );
    if (breadNotOffers) {
      isPending = breadNotOffers.sub_details.lender_response === AppSubLenderResponseE.pending;
    }
    if (isBread && isBreadError) {
      if (isPending) {
        return breadPendingMsg;
      } else {
        // errors
        return (
          <>
            <b>Proceeding with another provider</b>: By selecting "Additional Options" I agree that I am proceeding with
            additional financing or payment option provider(s).
          </>
        );
      }
    }
    if (isBread) {
      const hasOffersFromBread = offers.find((o) => checkIfBreadId(o?.lender?.lender_id));
      if (hasOffersFromBread) {
        //existing offer
        const isExistingAcc = hasOffersFromBread.offer_details?.fp_detailed?.map((fp) => fp.value);
        if (isExistingAcc?.includes('Existing Account Found')) {
          return (
            <>
              <b>Proceeding with another provider</b>: By selecting "Additional Options" I agree that I am proceeding
              with additional financing or payment option provider(s).
            </>
          );
        }
        //partial offer
        return (
          <>
            <b>Proceeding with another provider(s)</b>: By selecting "Additional Options" I agree that I am proceeding
            with additional financing or payment option provider(s). This may result in additional inquires on my credit
            report. Selecting additional options does not cancel the Petland Credit Card and it will still be sent to
            me.
          </>
        );
      } else {
        if (isPending) {
          return breadPendingMsg;
        }
        // declined
        return (
          <>
            <b>Proceeding with another provider(s)</b>: By selecting "Additional Options" I agree that I am proceeding
            with additional financing or payment options provider(s). This may result in additional inquries on my
            credit report.
          </>
        );
      }
    }
    return 'Would you like to move forward with additional providers?';
  };

  const buttonText = isBread ? 'Additional Options' : 'Continue';

  return (
    <>
    <ThemedCard sx={{ backgroundColor: '#F3F6FB', my: 2, mx: 3 }}>
      <CardContent>
        <Typography variant="body1" color="textSecondary" sx={{ marginBottom: '40px' }}>
          {getCardText()}
        </Typography>
        <StyledContainer>
          <Button
            variant="contained"
            color="warning"
            sx={{
              minWidth: '300px',
              backgroundColor: '#ACBAE3',
              ':hover': {
                backgroundColor: '#ACBAE3',
              },
              color: '#FFF',
            }}
            onClick={onClick}
          >
            {buttonText}
          </Button>
        </StyledContainer>
      </CardContent>
    </ThemedCard>
    <BreadMoveForwardModal
      open={modalOpen}
      onAdditionalOptions={handleAdditionalOptions}
      onClose={handleModalClose}
    />
    </>
  );
}
